import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardPrazo } from '../components/CardPrazo';
import { ModalDetalhe } from '../components/ModalDetalhe';
import { ehAdmin, useAuth, useMe, usePrazosSemana, useTheme } from '../hooks';
import type { ColunaKanban as ColunaKanbanType, PrazoCard } from '../types';
import { PT_DIA, VAR_COR, semanaISO, semanaToDatas } from '../utils';

type Tab = 'kanban' | 'caixa' | 'mais';
const TITULOS: Record<Tab, string> = {
  kanban: 'Kanban',
  caixa:  'Minha caixa',
  mais:   'Configurações',
};

// Configuração das 4 colunas do kanban horizontal mobile
const COLUNAS_CFG: {
  id: ColunaKanbanType;
  titulo: string;
  emoji: string;
  cor: 'vermelho' | 'azul_escuro' | 'verde' | 'cinza';
}[] = [
  { id: 'nao_feitos',       titulo: '📌 TRIAGEM INICIAL', emoji: '', cor: 'vermelho' },
  { id: 'em_andamento',     titulo: '📝 Em andamento',  emoji: '', cor: 'azul_escuro' },
  { id: 'pronto_protocolo', titulo: 'Pronto',        emoji: '🟢', cor: 'verde' },
  { id: 'concluidos',       titulo: 'Feito',         emoji: '⚪', cor: 'cinza' },
];

export function Mobile() {
  const [tab, setTab] = useState<Tab>('kanban');
  const [detalheId, setDetalheId] = useState<number | null>(null);
  const [colunaIdx, setColunaIdx] = useState(0);
  const [menuAberto, setMenuAberto] = useState(false);
  const trilhoRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: me } = useMe();
  const { tema, toggle } = useTheme();
  const { logout } = useAuth();

  const semana = semanaISO();
  const { data } = usePrazosSemana(semana);

  const colunasVisiveis = useMemo(() => {
    if (me?.papel === 'correspondente') {
      return COLUNAS_CFG.filter((c) => c.id === 'concluidos');
    }
    return COLUNAS_CFG;
  }, [me?.papel]);

  const todos = useMemo<PrazoCard[]>(
    () => data?.colunas.flatMap((c) => c.cards) ?? [],
    [data],
  );

  const cardsCaixa = useMemo<PrazoCard[]>(() => {
    if (!me) return [];
    if (!ehAdmin(me.papel))
      return todos.filter((c) => c.etiqueta === 'verde');
    return todos
      .filter(
        (c) =>
          c.etiqueta === 'vermelho' ||
          c.etiqueta === 'amarelo' ||
          c.atribuido_a?.id === me.id,
      )
      .sort((a, b) => a.data_vencimento.localeCompare(b.data_vencimento)
        || a.numero_processo.localeCompare(b.numero_processo));
  }, [todos, me]);

  // Cards agrupados pelas 4 colunas do kanban
  const cardsPorColuna = useMemo(() => {
    const out: Record<ColunaKanbanType, PrazoCard[]> = {
      nao_feitos: [], em_andamento: [], pronto_protocolo: [], concluidos: [],
    };
    for (const col of data?.colunas ?? []) {
      out[col.coluna] = col.cards;
    }
    return out;
  }, [data]);

  const corAvatar = me?.cor_etiqueta === 'azul_escuro'
    ? 'bg-etq-azulEscuro'
    : me?.cor_etiqueta === 'azul_claro'
    ? 'bg-etq-azulClaro'
    : 'bg-gray-500';

  // Detecta qual coluna está atualmente visível no trilho horizontal
  useEffect(() => {
    if (tab !== 'kanban') return;
    const el = trilhoRef.current;
    if (!el) return;
    const handler = () => {
      const w = el.clientWidth;
      const idx = Math.round(el.scrollLeft / w);
      setColunaIdx(Math.max(0, Math.min(3, idx)));
    };
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [tab]);

  function irParaColuna(i: number) {
    const el = trilhoRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * i, behavior: 'smooth' });
    setColunaIdx(i);
  }

  return (
    <div className={`pb-[70px] ${me?.papel === 'testador' ? 'testador-privacy' : ''}`}>
      <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-20">
        <div className="px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-blue-700 flex items-center justify-center text-white font-bold text-xs">
              A
            </div>
            <h1 className="text-base font-semibold">{TITULOS[tab]}</h1>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={toggle} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-300">
              {tema === 'dark' ? '☀️' : '🌙'}
            </button>
            <div className="relative ml-1">
              <button
                onClick={() => setMenuAberto((v) => !v)}
                className={`w-7 h-7 rounded-full ${corAvatar} text-white text-xs font-bold flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-400`}
                title="Perfil e opções"
                aria-label="Abrir menu do perfil"
              >
                {me?.nome[0] ?? '?'}
              </button>
              {menuAberto && (
                <>
                  {/* Backdrop p/ fechar ao clicar fora */}
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setMenuAberto(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-40 overflow-hidden">
                    <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {me?.nome ?? '—'}
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {me?.papel ?? ''}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setMenuAberto(false);
                        // ?force=desktop pula o redirect mobile do App.tsx
                        navigate('/semanal?force=desktop');
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 flex items-center gap-2"
                    >
                      🖥️ Modo desktop
                    </button>
                    <button
                      onClick={() => {
                        setMenuAberto(false);
                        navigate('/trocar-senha');
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 flex items-center gap-2"
                    >
                      🔑 Trocar senha
                    </button>
                    <button
                      onClick={() => {
                        setMenuAberto(false);
                        logout();
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-950/30 text-red-700 dark:text-red-400 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2"
                    >
                      🚪 Sair
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="px-4 pb-2 flex items-center justify-between text-xs">
          <span className="text-gray-500 dark:text-gray-400">
            {new Date().toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' })} · {semana}
          </span>
          <span className="text-gray-400 dark:text-gray-500">{todos.length} cards</span>
        </div>
      </header>

      <main className={tab === 'kanban' ? '' : 'p-3'}>
        {tab === 'kanban' && (
          <>
            {/* Indicador clicável das colunas — fica sticky abaixo do header */}
            {colunasVisiveis.length > 1 && (
              <nav className="sticky top-[88px] z-10 bg-gray-100 dark:bg-gray-900 px-3 py-2 flex gap-1.5">
                {colunasVisiveis.map((col, i) => {
                  const ativo = i === colunaIdx;
                  const total = cardsPorColuna[col.id]?.length ?? 0;
                  return (
                    <button
                      key={col.id}
                      onClick={() => irParaColuna(i)}
                      className={`flex-1 min-w-0 flex flex-col items-center justify-center px-1 py-1.5 rounded-md transition text-xs ${
                        ativo
                          ? 'bg-white dark:bg-gray-700 shadow-sm font-semibold ring-1'
                          : 'text-gray-550 dark:text-gray-400'
                      }`}
                      style={ativo ? { color: VAR_COR[col.cor] } : undefined}
                    >
                      <span className="text-sm leading-none">{col.emoji}</span>
                      <span className="mt-0.5 truncate w-full text-center">
                        {col.titulo} <span className="opacity-60">({total})</span>
                      </span>
                    </button>
                  );
                })}
              </nav>
            )}

            {/* Trilho com snap horizontal — uma coluna por viewport */}
            <div
              ref={trilhoRef}
              className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-none"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {colunasVisiveis.map((col) => {
                const cards = cardsPorColuna[col.id] ?? [];
                const destacar = col.id === 'pronto_protocolo';
                return (
                  <section
                    key={col.id}
                    className="w-screen flex-shrink-0 snap-center px-3 pt-2 pb-4"
                  >
                    <header
                      className={`rounded-t-lg px-3 py-2 flex items-center justify-between ${
                        destacar
                          ? 'bg-green-100/60 dark:bg-green-900/30 border-b border-green-200 dark:border-green-800'
                          : 'bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{col.emoji}</span>
                        <h2
                          className={`text-sm font-semibold ${
                            destacar ? 'text-green-800 dark:text-green-200 uppercase tracking-wide' : ''
                          }`}
                        >
                          {col.titulo}
                        </h2>
                      </div>
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          destacar ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-700'
                        }`}
                      >
                        {cards.length}
                      </span>
                    </header>
                    <div
                      className={`rounded-b-lg px-3 py-3 ${
                        destacar
                          ? 'bg-green-50 dark:bg-green-900/20 ring-2 ring-green-400 dark:ring-green-700 ring-inset'
                          : 'bg-white dark:bg-gray-800 shadow-sm'
                      }`}
                    >
                      {cards.length > 0 ? (
                        <div className="space-y-2">
                          {cards.map((c) => (
                            <CardPrazo
                              key={c.id_prazo}
                              card={c}
                              draggable={false}
                              onClick={() => me?.papel !== 'testador' && setDetalheId(c.id_prazo)}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-gray-400 dark:text-gray-500 py-8 text-sm italic">
                          — sem cards —
                        </p>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>

            {/* Hint inferior — só na primeira vez seria útil mas mantém leve */}
            <p className="text-[10px] text-center text-gray-400 dark:text-gray-500 mt-2 px-4">
              deslize ← / → pra navegar entre colunas · toque no card pra detalhes
            </p>
          </>
        )}

        {tab === 'caixa' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium uppercase">
              {cardsCaixa.length} cards relevantes pra você
            </p>
            <div className="space-y-2">
              {cardsCaixa.length > 0
                ? cardsCaixa.map((c) => (
                    <CardPrazo
                      key={c.id_prazo}
                      card={c}
                      draggable={false}
                      onClick={() => me?.papel !== 'testador' && setDetalheId(c.id_prazo)}
                    />
                  ))
                : <p className="text-center text-gray-400 py-8">Nada pra fazer. 🎉</p>}
            </div>
          </div>
        )}

        {tab === 'mais' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm divide-y divide-gray-200 dark:divide-gray-700">
            {me && (
              <div className="p-4 flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full ${corAvatar} text-white font-bold flex items-center justify-center text-lg`}>
                  {me.nome[0]}
                </div>
                <div>
                  <div className="font-semibold">{me.nome}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {me.email} · {me.papel}
                  </div>
                </div>
              </div>
            )}
            <button onClick={() => { location.href = '/trocar-senha'; }} className="w-full p-4 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex justify-between">
              Trocar senha<span>›</span>
            </button>
            <button onClick={toggle} className="w-full p-4 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex justify-between">
              Alternar tema<span>{tema === 'dark' ? '☀️' : '🌙'}</span>
            </button>
            <button
              onClick={() => {
                localStorage.setItem('viewPref', 'desktop');
                location.href = '/semanal';
              }}
              className="w-full p-4 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex justify-between"
            >
              Forçar layout desktop<span>›</span>
            </button>
            <button
              onClick={() => { logout(); location.href = '/login'; }}
              className="w-full p-4 text-left text-sm text-red-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Sair
            </button>
          </div>
        )}
      </main>

      {/* Bottom nav fixa */}
      <nav className="bottom-nav">
        {(['kanban', 'caixa', 'mais'] as Tab[])
          .filter((t) => !(t === 'caixa' && me?.papel === 'correspondente'))
          .map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={tab === t ? 'ativo' : ''}
            >
              <span className="text-lg">
                {t === 'kanban' ? '🗂' : t === 'caixa' ? '📥' : '⋯'}
              </span>
              <span>{TITULOS[t]}</span>
            </button>
          ))}
      </nav>

      {detalheId !== null && (
        <ModalDetalhe idPrazo={detalheId} onClose={() => setDetalheId(null)} />
      )}
    </div>
  );
}
